import React, { useEffect, useRef, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import ReactPlayer from 'react-player';
import { Button, IconButton, Typography } from '@mui/material';

import CloseIcon from '@mui/icons-material/CloseOutlined';
import PlayIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';

import { toSecond } from '../components/utilities';

const CorrectionsVideoPlayer = ({ onClose, video_url, start, end, name, requestedChanges, t }) => {
    const handle = useFullScreenHandle();
    const player = useRef(null);
    const hasSeekedRef = useRef(false);
    const [playRate, setPlayRate] = useState(1);
    const [play, setPlay] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);

    const fastVideo = (param) => {
        player.current && player.current.seekTo(currentTime + param);
    };

    const onProgress = (seconds) => {
        setCurrentTime(seconds);

        if (seconds >= toSecond(end)) {
            setPlay(false);
            onClose();
        } else if (seconds <= toSecond(start)) {
            if (playRate !== 1) {
                setPlay(false);
                setPlayRate(1);
            }
        }
    };

    useEffect(() => {
        // Reset seek state when URL or start changes (e.g., after refresh)
        hasSeekedRef.current = false;
        if (player.current && start) {
            const startSec = toSecond(start);
            // In case onReady hasn't fired yet, try a best-effort seek
            player.current.seekTo(startSec, 'seconds');
        }
    }, [video_url, start]);

    const handleReady = () => {
        if (!hasSeekedRef.current && player.current) {
            const startSec = toSecond(start || '00:00:00');
            player.current.seekTo(startSec, 'seconds');
            hasSeekedRef.current = true;
        }
    };

    const handleStart = () => {
        if (!hasSeekedRef.current && player.current) {
            const startSec = toSecond(start || '00:00:00');
            player.current.seekTo(startSec, 'seconds');
            hasSeekedRef.current = true;
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}
        >
            <div style={{ width: '80%', margin: 'auto', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <FullScreen handle={handle}>
                    <div style={{ width: '100%', margin: 'auto' }}>
                        <div className="player-wrapper">
                            <ReactPlayer
                                className="react-player"
                                url={video_url}
                                ref={player}
                                onPlay={() => setPlay(true)}
                                onPause={() => setPlay(false)}
                                onReady={handleReady}
                                onStart={handleStart}
                                onProgress={(p) => onProgress(p.playedSeconds)}
                                playing={play}
                                controls={false}
                                playbackRate={playRate}
                                width="100%"
                                height="100%"
                            />
                        </div>
                    </div>
                    {/* Requested changes overlay (top-left over video) */}
                    {requestedChanges && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '12px',
                                left: '16px',
                                background: 'rgba(0, 0, 0, 0.7)',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                maxWidth: '60%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}
                        >
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#ffffff', mb: 0.5 }}>
                                    {t ? t('Requested Changes') : 'Requested Changes'}:
                                </Typography>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 400, color: '#ffffff' }}>
                                    <strong>{t ? t('Player') : 'Player'}:</strong> {requestedChanges.new_player_name} (#{requestedChanges.new_jersey_number})
                                </Typography>
                                {requestedChanges.requested_action_name && (
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 400, color: '#ffffff' }}>
                                        <strong>{t ? t('Action') : 'Action'}:</strong> {requestedChanges.requested_action_name}
                                    </Typography>
                                )}
                                {requestedChanges.requested_action_type_name && (
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 400, color: '#ffffff' }}>
                                        <strong>{t ? t('Type') : 'Type'}:</strong> {requestedChanges.requested_action_type_name}
                                    </Typography>
                                )}
                                {requestedChanges.requested_action_result_name && (
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 400, color: '#ffffff' }}>
                                        <strong>{t ? t('Result') : 'Result'}:</strong> {requestedChanges.requested_action_result_name}
                                    </Typography>
                                )}
                                {requestedChanges.requested_court_area_name && (
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 400, color: '#ffffff' }}>
                                        <strong>{t ? t('Court Area') : 'Court Area'}:</strong> {requestedChanges.requested_court_area_name}
                                    </Typography>
                                )}
                                {requestedChanges.requested_inside_pain !== null && (
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 400, color: '#ffffff' }}>
                                        <strong>{t ? t('Inside Box') : 'Inside Box'}:</strong> {requestedChanges.requested_inside_pain ? 'Yes' : 'No'}
                                    </Typography>
                                )}
                                {requestedChanges.requested_start_time && requestedChanges.requested_end_time && (
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 400, color: '#ffffff' }}>
                                        <strong>{t ? t('Time') : 'Time'}:</strong> {requestedChanges.requested_start_time} - {requestedChanges.requested_end_time}
                                    </Typography>
                                )}
                        </div>
                    )}
                    {/* Close button (top-right over video) */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            cursor: 'pointer',
                            background: 'rgba(0,0,0,0.55)',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={onClose}
                    >
                        <CloseIcon sx={{ color: '#fff' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', position: 'absolute', left: 0, bottom: '16px', justifyContent: 'space-between', width: '100%', padding: '0 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <IconButton style={{ color: '#ffffff', backgroundColor: '#80808069' }} onClick={() => fastVideo(-3)}>
                                <FastRewindIcon color="#ffffff" />
                            </IconButton>
                            <Button variant="outlined" sx={{ width: '60px', color: '#ffffff' }} onClick={() => setPlayRate(0.5)}>
                                Slow
                            </Button>
                            <IconButton
                                onClick={() => {
                                    if (playRate === 1) setPlay((p) => !p);
                                    else setPlayRate(1);
                                }}
                                style={{ color: '#ffffff', backgroundColor: '#80808069' }}
                            >
                                {play && playRate === 1 ? <PauseIcon /> : <PlayIcon />}
                            </IconButton>
                            <Button variant="outlined" sx={{ width: '60px', color: '#ffffff' }} onClick={() => setPlayRate((s) => s + 0.5)}>
                                Fast
                            </Button>
                            <IconButton style={{ color: '#ffffff', backgroundColor: '#80808069' }} onClick={() => fastVideo(3)}>
                                <FastForwardIcon color="#ffffff" />
                            </IconButton>
                        </div>
                        {name !== '' && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 12px', background: '#80808069' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '16px', fontWeight: 500, color: '#ffffff' }}>{name}</Typography>
                            </div>
                        )}
                    </div>
                </FullScreen>
            </div>
        </div>
    );
};

export default CorrectionsVideoPlayer;
